import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../lib/api'
import type { JobListItem } from '../../types/api'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import FileUpload from '../../components/ui/FileUpload'
import { JobStatusBadge, JobTypeBadge } from '../Jobs'
import SourcingAgentModal from '../../components/SourcingAgentModal'

type BasicForm = {
  title: string
  employer: string
  location: string
  employment_type: string
  job_duration: string
  working_languages: string
  salary_range: string
  positions: string
  application_deadline: string
  source_url: string
  source_id: string
  description: string
  status: string
  type: string
  required_topics: string
  preferred_topics: string
}

const EMPTY_FORM: BasicForm = {
  title: '', employer: '', location: '', employment_type: '',
  job_duration: '', working_languages: '', salary_range: '', positions: '', application_deadline: '',
  source_url: '', source_id: '',
  description: '', status: 'Draft', type: 'Listed', required_topics: '', preferred_topics: '',
}

export default function HrJobs() {
  const navigate = useNavigate()
  const [jobs, setJobs] = useState<JobListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<BasicForm>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [publishing, setPublishing] = useState<string | null>(null)
  const [sourcingJob, setSourcingJob] = useState<{ id: string; title: string } | null>(null)

  const loadJobs = () => {
    api.jobs.list()
      .then((res) => {
        setJobs(res.data.filter((j) => j.type === 'Listed' || j.type === 'Exclusive'))
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadJobs() }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)
    setSubmitting(true)
    try {
      const required_topics = form.required_topics.split(',').map((s) => s.trim()).filter(Boolean)
      const preferred_topics = form.preferred_topics.split(',').map((s) => s.trim()).filter(Boolean)
      const working_languages = form.working_languages.split(',').map((s) => s.trim()).filter(Boolean)
      const positions = form.positions ? parseInt(form.positions, 10) : undefined
      await api.jobs.createBasic({
        title: form.title,
        employer: form.employer,
        ...(form.location && { location: form.location }),
        ...(form.employment_type && { employment_type: form.employment_type }),
        ...(form.job_duration && { job_duration: form.job_duration }),
        ...(working_languages.length && { working_languages }),
        ...(form.salary_range && { salary_range: form.salary_range }),
        ...(positions && { positions }),
        ...(form.application_deadline && { application_deadline: form.application_deadline }),
        ...(form.source_url && { source_url: form.source_url }),
        ...(form.source_id && { source_id: form.source_id }),
        ...(form.description && { description: form.description }),
        ...(required_topics.length && { required_topics }),
        ...(preferred_topics.length && { preferred_topics }),
        status: form.status,
        type: form.type,
      })
      setShowCreate(false)
      setForm(EMPTY_FORM)
      setLoading(true)
      loadJobs()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Create failed')
    } finally {
      setSubmitting(false)
    }
  }

  const handlePublish = async (jobId: string) => {
    setPublishing(jobId)
    try {
      await api.jobs.updateFrontmatter(jobId, { status: 'Open' })
      loadJobs()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed')
    } finally {
      setPublishing(null)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-7 w-32 bg-surface-lowest rounded" />
        </div>
        <div className="border border-border-subtle rounded-lg overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-surface-lowest border-t border-border-subtle first:border-t-0" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-foreground">My jobs</h1>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setShowUpload(true)}>Upload job</Button>
            <Button variant="primary" onClick={() => setShowCreate(true)}>Post new job</Button>
          </div>
        </div>

        {error && <p className="text-accent-error text-sm">{error}</p>}

        {jobs.length === 0 ? (
          <div className="border border-dashed border-border-subtle rounded-lg p-12 text-center">
            <p className="text-text-muted text-sm">No jobs yet. Post your first job to start matching with candidates.</p>
          </div>
        ) : (
          <div className="border border-border-subtle rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle bg-surface-lowest">
                  <th className="text-left px-4 py-3 text-xs text-text-muted font-medium">Job</th>
                  <th className="text-left px-4 py-3 text-xs text-text-muted font-medium hidden md:table-cell">Type</th>
                  <th className="text-left px-4 py-3 text-xs text-text-muted font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-xs text-text-muted font-medium hidden lg:table-cell">Topics</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    className="border-b border-border-subtle last:border-b-0 hover:bg-surface-lowest transition-colors"
                  >
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/hr/jobs/${job.id}`)}
                        className="text-left hover:text-primary transition-colors"
                      >
                        <p className="font-medium text-foreground">{job.title}</p>
                        <p className="text-xs text-text-muted">{job.employer}</p>
                      </button>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <JobTypeBadge type={job.type} />
                    </td>
                    <td className="px-4 py-3">
                      <JobStatusBadge status={job.status} />
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {job.required_topics.slice(0, 3).map((t) => (
                          <Badge key={t} variant="preferred">{t}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {job.status === 'Draft' && (
                          <Button
                            variant="primary"
                            onClick={() => handlePublish(job.id)}
                            disabled={publishing === job.id}
                            className="text-xs"
                          >
                            {publishing === job.id ? 'Publishing...' : 'Publish'}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          onClick={() => setSourcingJob({ id: job.id, title: job.title })}
                          disabled={job.status !== 'Open'}
                          className="text-xs"
                        >
                          Source
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => navigate(`/hr/jobs/${job.id}`)}
                          className="text-xs"
                        >
                          View →
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={showUpload} onClose={() => setShowUpload(false)} title="Upload job posting">
        <FileUpload
          onUpload={async (file) => {
            await api.jobs.uploadFile(file)
            setShowUpload(false)
            setLoading(true)
            loadJobs()
          }}
          accept=".pdf,.txt,.md,.doc,.docx"
          label="Drop a job posting file or click to browse"
        />
      </Modal>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Post new job">
        <form onSubmit={handleCreate} className="space-y-3">
          {formError && <p className="text-accent-error text-sm">{formError}</p>}
          {[
            { key: 'title', label: 'Job title', placeholder: 'Senior Engineer', required: true },
            { key: 'employer', label: 'Company', placeholder: 'Acme Corp', required: true },
            { key: 'location', label: 'Location', placeholder: 'Helsinki, Finland' },
            { key: 'employment_type', label: 'Employment type', placeholder: 'Full-time' },
            { key: 'job_duration', label: 'Duration', placeholder: '3-6 months / permanent' },
            { key: 'working_languages', label: 'Working languages (comma-separated)', placeholder: 'Finnish, English' },
            { key: 'salary_range', label: 'Salary range', placeholder: '4000-5000 €/month' },
            { key: 'positions', label: 'Positions', placeholder: '1', type: 'number' },
            { key: 'application_deadline', label: 'Application deadline', placeholder: 'YYYY-MM-DD' },
            { key: 'source_url', label: 'Source URL', placeholder: 'https://example.com/job' },
            { key: 'source_id', label: 'Source ID', placeholder: 'External job ID' },
            { key: 'required_topics', label: 'Required skills (comma-separated)', placeholder: 'TypeScript, React' },
            { key: 'preferred_topics', label: 'Preferred skills (comma-separated)', placeholder: 'Docker, AWS' },
          ].map(({ key, label, placeholder, type = 'text', required }) => (
            <div key={key}>
              <label className="label-base">{label}{required && ' *'}</label>
              <input
                type={type}
                value={form[key as keyof BasicForm]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                className="input-base"
                required={required}
              />
            </div>
          ))}
          <div>
            <label className="label-base">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Job description..."
              rows={4}
              className="input-base resize-none"
            />
          </div>
          <div>
            <label className="label-base">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              className="input-base"
            >
              {['Listed', 'Exclusive'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex gap-2 pt-3">
            <Button type="submit" disabled={submitting || !form.title || !form.employer}>
              {submitting ? 'Creating...' : 'Create job'}
            </Button>
            <Button variant="ghost" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <SourcingAgentModal
        isOpen={sourcingJob !== null}
        onClose={() => setSourcingJob(null)}
        jobId={sourcingJob?.id || ''}
        jobTitle={sourcingJob?.title || ''}
      />
    </>
  )
}
